import { useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { Map, GitBranch, Languages, ListChecks, Globe, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader } from "@/components/CategoryBadge";
import { TemplateManager, WorldSettingsPicker } from "@/components/TemplateManager";
import { DeleteUniverse } from "@/components/DeleteUniverse";
import { SuggestionPolicy } from "@/components/comments/SuggestionPolicy";
import { DismissedSuggestions } from "@/components/DismissedSuggestions";
import { api, errMsg } from "@/lib/api";
import { useInvalidate } from "@/lib/queries";
import { OfflineAccess } from "@/components/offline/OfflineAccess";

const Card = ({ title, sub, children, testid }) => (
  <section className="rounded-md border border-[#2D3442] bg-[#13171F] p-6" data-testid={testid}>
    <h2 className="font-display text-2xl text-[#F3EBDD]">{title}</h2>
    {sub && <p className="mt-1 mb-4 text-sm text-[#A8A090]">{sub}</p>}
    <div className={sub ? "" : "mt-4"}>{children}</div>
  </section>
);

const MODULES = [["maps", "Maps", "Atlas, pins and regions", Map], ["timelines", "Timelines", "Chronicle, events and time travel", GitBranch], ["languages", "Languages", "Grammar, dictionary and script", Languages]];

function General({ universe, isOwner, patch }) {
  const [name, setName] = useState(universe.name);
  const [description, setDescription] = useState(universe.description || "");
  const dirty = name.trim() !== universe.name || description !== (universe.description || "");
  const link = `${window.location.origin}/p/${universe.id}`;
  return (
    <Card title="General" testid="settings-general">
      <div className="grid gap-4">
        <div className="space-y-1.5"><Label>Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} disabled={!isOwner} data-testid="settings-name" /></div>
        <div className="space-y-1.5"><Label>Description</Label><Textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} disabled={!isOwner} data-testid="settings-description" /></div>
        {isOwner && <div><Button onClick={() => patch({ name, description }, "Universe updated")} disabled={!dirty || !name.trim()} data-testid="settings-save-general">Save</Button></div>}
        <div className="flex items-center justify-between gap-4 border-t border-[#2D3442] pt-4">
          <div>
            <div className="flex items-center gap-2 text-sm text-[#F3EBDD]"><Globe className="h-4 w-4" />Public read-only link</div>
            <p className="text-xs text-[#A8A090]">Anyone with the link can read the Lore Bible, maps, timelines and languages.</p>
          </div>
          <Switch checked={!!universe.is_public} disabled={!isOwner} onCheckedChange={(v) => patch({ is_public: v }, v ? "Universe is public" : "Universe is private")} data-testid="settings-public-toggle" />
        </div>
        {universe.is_public && (
          <div className="flex items-center gap-2">
            <Input readOnly value={link} className="font-mono-lf text-xs" data-testid="settings-public-link" />
            <Button size="icon" variant="ghost" onClick={() => { navigator.clipboard?.writeText(link); toast.success("Link copied"); }} data-testid="settings-copy-link"><Copy className="h-4 w-4" /></Button>
          </div>
        )}
      </div>
    </Card>
  );
}

export default function SettingsPage() {
  const { universe, canEdit } = useOutletContext();
  const navigate = useNavigate();
  const invalidate = useInvalidate();
  const [templating, setTemplating] = useState(false);
  const isOwner = universe.role === "owner";
  const patch = async (body, msg) => {
    try {
      await api.patch(`/universes/${universe.id}`, body);
      invalidate(["universe", universe.id], ["universes"]);
      toast.success(msg);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const mods = universe.modules;
  return (
    <div className="max-w-4xl px-4 sm:px-8 lg:px-12 py-10">
      <PageHeader eyebrow={universe.name} title="Universe settings" />
      <div className="space-y-6">
        <General key={universe.updated_at} universe={universe} isOwner={isOwner} patch={patch} />
        {isOwner && <OfflineAccess universe={universe} />}
        <Card title="World type" sub="Pick every genre that fits. It decides which recommended fields apply, e.g. Planet for sci-fi or Race for fantasy." testid="settings-world-type">
          <WorldSettingsPicker uid={universe.id} compact={!canEdit} disabled={!canEdit} />
        </Card>
        <Card title="Suggestions" sub="Decide who can accept or reject co-authors' suggested edits." testid="settings-suggestions-card">
          <SuggestionPolicy universe={universe} isOwner={isOwner} patch={patch} />
        </Card>
        <Card title="Sections" sub="Switch off what this universe doesn't need. Turned-off sections are hidden from the sidebar, exports and AI; their data is kept." testid="settings-sections">
          <div className="divide-y divide-[#2D3442]">
            {MODULES.map(([k, label, sub, Icon]) => (
              <div key={k} className="flex items-center justify-between py-3">
                <div className="flex items-center gap-3"><Icon className="h-4 w-4 text-[#F59E0B]" /><div><div className="text-sm text-[#F3EBDD]">{label}</div><div className="text-xs text-[#A8A090]">{sub}</div></div></div>
                <Switch checked={mods[k]} disabled={!isOwner} onCheckedChange={(v) => patch({ modules: { ...mods, [k]: v } }, `${label} ${v ? "on" : "off"}`)} data-testid={`settings-module-${k}`} />
              </div>
            ))}
          </div>
          {!isOwner && <p className="mt-2 text-xs text-[#A8A090]">Only the owner can switch sections.</p>}
        </Card>
        {canEdit && (
          <Card title="Recommended info" sub="Choose which fields each category should record." testid="settings-templates">
            <Button variant="outline" onClick={() => setTemplating(true)} data-testid="settings-open-templates"><ListChecks className="h-4 w-4 mr-1.5" />Edit category templates</Button>
          </Card>
        )}
        {canEdit && (
          <Card title="Dismissed suggestions" sub="Things you told the lore review not to suggest as new entries. Restore one to let it be suggested again." testid="settings-dismissed">
            <DismissedSuggestions uid={universe.id} />
          </Card>
        )}
        {isOwner && <DeleteUniverse universe={universe} onDeleted={() => navigate("/")} />}
      </div>
      {templating && <TemplateManager uid={universe.id} onOpenChange={setTemplating} />}
    </div>
  );
}
